import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { AppFrame } from "./AppFrame";

const RoutesShot = lazy(() => import("./RoutesShot"));

/**
 * "See both routes" (the prototype's routesArt, v32/v33): the real net worth card from the app,
 * not a drawing. It is authored at a fixed width (880 px; 390 px on a phone, the width the
 * mobile layer gives app previews) and scaled to the panel. The card, the chart code and the
 * fixtures are a separate chunk, loaded in an idle callback once the panel is within 1200 px of
 * the viewport.
 */
export const ROUTES_SHOT = { width: 880, phoneWidth: 390, phoneBelow: 701, margin: "1200px" };

/** The frame's own rules: the card sits flush in the panel. */
const FLUSH = ".card{border:0!important;box-shadow:none!important;margin:0!important}";

export function RoutesPanel() {
  const wrap = useRef<HTMLDivElement>(null);
  const mini = useRef<HTMLDivElement>(null);
  const [built, setBuilt] = useState(false);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setBuilt(true);
      return;
    }
    let idle = 0;
    const io = new IntersectionObserver(
      (es) => {
        if (!es.some((e) => e.isIntersecting)) return;
        io.disconnect();
        const ric = window.requestIdleCallback;
        idle = ric
          ? ric(() => setBuilt(true), { timeout: 1200 })
          : window.setTimeout(() => setBuilt(true), 1);
      },
      { rootMargin: ROUTES_SHOT.margin },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      if (window.cancelIdleCallback) window.cancelIdleCallback(idle);
      clearTimeout(idle);
    };
  }, []);

  // routesFit: scale the authored width to the panel, and hold the panel at the scaled height.
  const fit = useCallback(() => {
    const el = wrap.current;
    const m = mini.current;
    if (!el || !m || !el.clientWidth) return;
    const authored =
      window.innerWidth < ROUTES_SHOT.phoneBelow ? ROUTES_SHOT.phoneWidth : ROUTES_SHOT.width;
    m.style.setProperty("--rshot-w", `${authored}px`);
    m.style.setProperty("--rshot-scale", (el.clientWidth / authored).toFixed(4));
    el.style.height = `${Math.round(m.getBoundingClientRect().height)}px`;
  }, []);

  useEffect(() => {
    if (!built) return;
    const m = mini.current;
    fit();
    // The card arrives with the lazy chunk; refit whenever its height changes.
    const ro = "ResizeObserver" in window && m ? new ResizeObserver(() => fit()) : null;
    if (ro && m) ro.observe(m);
    let t: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      clearTimeout(t);
      t = setTimeout(fit, 140);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      clearTimeout(t);
      ro?.disconnect();
      window.removeEventListener("resize", onResize);
    };
  }, [built, fit]);

  return (
    <div
      className="rshotwrap"
      ref={wrap}
      role="img"
      aria-label="The net worth page from the app: two projected paths, the current route and the route Abi picks."
      data-testid="routes-shot"
    >
      {built && (
        <AppFrame frameRef={mini} extraCss={FLUSH}>
          <Suspense fallback={null}>
            <RoutesShot />
          </Suspense>
        </AppFrame>
      )}
    </div>
  );
}
